import { IntentKind, makeIntentBuilderFunction } from '../intents/common';
import {
    type FlowAction,
    type FlowActionDestination,
    makeFlowAction,
} from '../models';
import { Bag } from '../dependencies/bag';
import type { ActionMetrics, Opt } from '@jet/environment';
import { ObjectGraph } from '@jet/environment/dependencies/object-graph';

// Make Intent Builder Functions for different types
import { makeAlbumPageIntent } from '@jet-app/music-classical/intents/album-page/AlbumPageIntent';
import { makeArtistPageIntent } from '@jet-app/music-classical/intents/artist-page/ArtistPageIntent';
import { makePlaylistPageIntent } from '@jet-app/music-classical/intents/playlist-page/PlaylistPageIntent';
import { makeSeeAllPageIntent } from '@jet-app/music-classical/intents/see-all/SeeAllPageIntent';
import { makeBrowsePlaylistsPageIntent } from '@jet-app/music-classical/intents/browse-page/detail/playlists/BrowsePlaylistsPageIntent';

// Library Intent Builder Functions
import { makeLibraryPlaylistPageIntent } from '@jet-app/music-classical/intents/library/playlist/LibraryPlaylistPageIntent';

import type { ClassicalShelfItem, ListAction } from '../api/types';
import { getId, getRouteSegment } from './transforms/url';
import {
    DETAIL_PAGE_TYPE_ALLOWLIST,
    DetailPageType,
    makeDetailPageIntent,
} from '../intents/detail-page/DetailPageIntent';
import { makeBrowseDetailPageIntent } from '../intents/browse-page/detail/BrowseDetailPageIntent';

export function createFlowActionForItem(
    item: ClassicalShelfItem,
    objectGraph: ObjectGraph,
    additionalParams = {},
): FlowAction | void {
    // TODO: See how to handle/display See All Works inline links
    //       For now, do not create a segue for them.
    //       rdar://138055657 ([Classical on Web] See All Work Views)
    if (item.action?.url?.includes('/works?')) {
        return undefined;
    }

    const type = getItemType(item, objectGraph);
    const intentBuilderFn = getIntentBuilderFnForType(
        getLibraryItemTypeForAction(item?.action) ?? type,
        objectGraph,
    );

    let intentOptions = {
        $kind: item.type,
        id: getId(item.action?.url),
        ...additionalParams,
        ...(DETAIL_PAGE_TYPE_ALLOWLIST.includes(type)
            ? { pageType: type }
            : {}),
    };

    if (intentBuilderFn) {
        // Inline See All links are handled differently
        if (type === 'seeAll') {
            intentOptions = {
                $kind: IntentKind.SEE_ALL_PAGE,
                section: item.action?.location,
                query: item.action?.url,
            };
        }

        return createFlowAction(intentBuilderFn, intentOptions, objectGraph);
    }
}

/**
 * Creates a flow action when handed an `action` object from a Classical API
 * action object.
 * @param {ActionModel} action Action from the API response (composerAction, contextMenuAction, etc.)
 * @param {ObjectGraph} objectGraph
 * @param {Object} additionalParams Additional parameters to include in the flow action
 * @param {Boolean} [useAbsoluteUrls=false] Flag to set intent urls as absolute urls.  Defaults to generating relative urls.
 * @returns {FlowAction|void}
 */
export function createFlowActionForAction(
    action,
    objectGraph,
    additionalParams = {},
    useAbsoluteUrls = false,
): Opt<FlowAction> {
    let type: string;

    if (action?.url?.includes('/charts')) {
        type = 'charts';
    } else if (action?.url?.includes('/album/')) {
        type = 'album';
    } else {
        type = action?.screenType;
    }

    const intentBuilderFn = getIntentBuilderFnForType(
        getLibraryItemTypeForAction(action) ?? type,
        objectGraph,
    );

    if (intentBuilderFn) {
        let intentOptions = {
            $kind: type,
            id: getId(action?.url),
            url: (action?.url as string)?.replace(
                /\/query\/view/,
                useAbsoluteUrls ? 'https://classical.music.apple.com' : '',
            ),
            ...additionalParams,
            ...(DETAIL_PAGE_TYPE_ALLOWLIST.includes(type)
                ? { pageType: type }
                : {}),
        };

        return createFlowAction(intentBuilderFn, intentOptions, objectGraph);
    }
}

export function createFlowAction(
    intentBuilderFn: ReturnType<typeof makeIntentBuilderFunction>,
    intentOptions: { [key: string]: unknown },
    objectGraph: ObjectGraph,
    metrics?: ActionMetrics,
): FlowAction {
    const countryCode = Bag.countryCode(objectGraph);
    const language = Bag.languageTag(objectGraph);
    const { url, ...rest } = intentOptions;

    return makeFlowAction(
        intentBuilderFn({
            ...rest,
            // Used to generate URL previews on anchor elements.  Accessed by extractActionHref() in
            // https://github.pie.apple.com/amp-web/media-apps/blob/main/apps/music-classical/src/jet/utils.ts
            contentDescriptor: !!url ? { url } : undefined,
            storefront: countryCode,
            language,
        }) as unknown as FlowActionDestination,
        metrics ?? { data: [], custom: {} },
    );
}

export function getIntentBuilderFnForType(
    type: string,
    objectGraph?: ObjectGraph,
): ReturnType<typeof makeIntentBuilderFunction> {
    const map = {
        'album-with-actions': makeAlbumPageIntent,
        album: makeAlbumPageIntent,
        artist: makeArtistPageIntent,
        playlist: makePlaylistPageIntent,
        seeAll: makeSeeAllPageIntent,
        'library-playlist': makeLibraryPlaylistPageIntent,
        editorialPlaylistContainer: makeBrowsePlaylistsPageIntent,
        catalog: makeBrowseDetailPageIntent,
        work: makeDetailPageIntent,
    };

    return DETAIL_PAGE_TYPE_ALLOWLIST.includes(type as DetailPageType)
        ? makeDetailPageIntent
        : map[type];
}

export function getItemType(
    item: ClassicalShelfItem,
    _objectGraph: ObjectGraph,
): string {
    if (isLibraryItem(item)) {
        return item.type;
    }

    // Using screenType for multirooms will produce the wrong url segment
    return item.action?.screenType === 'multiRoom'
        ? 'multi-room'
        : item.action?.screenType;
}

export function getLibraryItemTypeForAction(action: ListAction): string | null {
    // Only signal in the action that this wants to navigate to Library View of any entity is the URL itself
    if (getRouteSegment(action?.url)?.includes('library-')) {
        return `library-${action?.screenType}`;
    }

    return null;
}

export function isLibraryItem(item: ClassicalShelfItem) {
    // If the type contains the `library-` prefix, it's a library type &
    // we should not use the item.action.screenType because screenType will still be 'playlist', 'album' etc
    return item.type?.includes('library-');
}
